import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, ReceiptText } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ContactFormModal, type ContactFormValues } from "@/components/domains/ContactFormModal";
import { DraftOffer, DraftSavedNote } from "@/components/ui/FormDraft";
import { useFormDraft, type FormDraftApi } from "@/lib/form-drafts";
import { useUser } from "@/auth/session";
import { PayIcon } from "@/components/billing/PayIcon";
import { PAY_METHOD_LABELS } from "@/components/billing/PaymentMethodSelector";
import { RenewalRiskWarning } from "@/components/billing/SaveCardChoice";
import { PoweredByStripe } from "@/components/billing/PoweredByStripe";
import { useWalletPayment } from "@/components/billing/useWalletPayment";
import {
  CardBadge,
  PaymentMethodModal,
  defaultMethod,
  usePaymentMethods,
  type WalletChoice,
} from "@/components/domains/shared";
import { PromotionCode, couponFieldError, isCouponReason } from "@/components/domains/PromotionCode";
import {
  ErrorAlert,
  ErrorState,
  ErrorSummary,
  focusControl,
  FullPageSpinner,
  cx,
  Badge,
  Field,
  Input,
  CONTROL_LG,
  Button,
  buttonClass,
  Breadcrumb,
  Section,
  Toggle,
} from "@/components/ui";
import {
  CONTACT_MAX,
  ROLE_LABELS,
  attemptEnded,
  domainKeys,
  domainsApi,
  endingOf,
  hasPromotion,
  joinPhone,
  longDate,
  newCheckoutKey,
  termOptions,
  useCart,
  type Cart,
  type CartItem,
  type CheckoutInput,
  type Contact,
  type ContactInput,
  type CouponPreview,
  type Order,
  type OtherRole,
  type TldField,
} from "@/lib/domains";
import { civilDay, formatMoney } from "@/lib/format";
import { ApiError } from "@/lib/http";
import { COUNTRIES, countryName, payableNow } from "@/lib/payments";
import { SUPPORT_EMAIL } from "@/lib/support";
import type { PaymentMethod } from "@/lib/types";

import {
  DIAL_CODES,
  DIAL_OPTIONS,
  clockTime,
  holdProblem,
  priceChange,
  reasonOf,
  safeSession,
  takenKeywords,
  useNow,
} from "./purchase/helpers";
import { cartRoles, chosenRoles, namesWithout } from "./purchase/contactRoles";
import { NoVatNote, OfferPill, UsualPrice } from "./purchase/Pricing";
import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import {
  DomainIcon,
  Heading,
  HeadTile,
  DomainPage,
  TwoColumns,
  Notice,
  Hint,
} from "@/components/domains/parts";

interface RegistrantForm {
  first_name: string;
  last_name: string;
  organization_name: string;
  email: string;
  dial: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}
type FormErrors = Partial<Record<keyof RegistrantForm, string>>;

interface Draft {
  /** A saved registrant; `null` = new details below; absent = not chosen yet (first saved one). */
  contactId?: string | null;
  useBusiness: boolean;
  form: RegistrantForm;
  privacy: boolean;
  autoRenew: boolean;
  paymentMethodId: string | null;
  /** Registry fields typed here, by cart item id then key (the cart holds what was saved). */
  tldFields?: Record<string, Record<string, string>>;
  /** A registry field checkout said is missing (TLD_FIELDS_REQUIRED), to point at on the details step. */
  flaggedField?: { key: string; message: string } | null;
  /** Saved contacts chosen for the other roles; a role left out is the registrant. */
  roleContacts?: Partial<Record<OtherRole, string>>;
}

/** What the server draft keeps ("Continue where you left off" on any device): no payment choice. */
type DraftDetails = Omit<Draft, "paymentMethodId" | "flaggedField">;

/** Admin, technical and billing contacts: the registrant unless the customer chooses a saved contact or adds one. */
function OtherContacts({
  cart,
  draft,
  setDraft,
  contacts,
}: Pick<StepProps, "cart" | "draft" | "setDraft" | "contacts">) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [adding, setAdding] = useState<OtherRole | null>(null);
  const create = useMutation({
    mutationFn: (v: ContactFormValues) => domainsApi.createContact(orgId, v),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ["domains", orgId, "contacts"] });
      if (adding) choose(adding, saved.id);
      setAdding(null);
    },
  });
  const roles = cartRoles(cart.items);
  if (roles.length === 0) return null;
  const choose = (role: OtherRole, id: string) =>
    setDraft((d) => {
      const next = { ...d.roleContacts };
      if (id) next[role] = id;
      else delete next[role];
      return { ...d, roleContacts: next };
    });
  return (
    <Section
      title="Other contacts"
      subtitle="Registries contact these people about the domain. They're the registrant unless you choose someone else."
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-3">
        {roles.map((role) => {
          const without = namesWithout(cart.items, role);
          return (
            <Field
              key={role}
              label={ROLE_LABELS[role]}
              hint={
                without.length > 0
                  ? `${without.join(", ")} ${without.length === 1 ? "has" : "have"} no ${ROLE_LABELS[role].toLowerCase()}: the registrant is used there`
                  : undefined
              }
            >
              {({ id }) => (
                <select
                  id={id}
                  value={draft.roleContacts?.[role] ?? ""}
                  onChange={(e) => {
                    if (e.target.value === NEW_CONTACT) {
                      create.reset();
                      setAdding(role);
                    } else choose(role, e.target.value);
                  }}
                  className={CONTROL_LG}
                >
                  <option value="">Same as the registrant</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name + (c.organization_name ? ` · ${c.organization_name}` : "")} · {c.email}
                    </option>
                  ))}
                  <option value={NEW_CONTACT}>New contact…</option>
                </select>
              )}
            </Field>
          );
        })}
      </div>
      <ContactFormModal
        key={adding ?? "closed"}
        open={adding !== null}
        onClose={() => setAdding(null)}
        title={adding ? `New ${ROLE_LABELS[adding].toLowerCase()}` : "New contact"}
        subtitle="Saved to your contacts and chosen for this role."
        submitLabel="Save and use"
        onSubmit={(v) => create.mutate(v)}
        pending={create.isPending}
        error={create.error}
      />
    </Section>
  );
}

/** The role picker's "add new details" choice (never a contact id). */
const NEW_CONTACT = "__new__";

// ── registry fields ─────────────────────────────────────────────────────────
/** Cart lines whose registry asks for extra values (e.g. the language of a .eu registration). */
function needsFields(items: CartItem[]): CartItem[] {
  return items.filter((i) => i.kind === "register" && i.required_fields.length > 0);
}

/** Cart lines whose ending needs a local presence: the customer confirms it or buys the trustee service. */
function needsPresence(items: CartItem[]): CartItem[] {
  return items.filter((i) => i.kind === "register" && i.local_presence !== "none");
}

const presenceErrorKey = (item: CartItem) => `${item.id}:presence`;
/** The id of the first control of a line's local-presence choice, so the summary can link to (and focus) it. */
const presenceFieldId = (item: CartItem) => `local-presence-${item.id}`;

/** A value that fits a staff-defined field's format rule (the rule must match the whole value). */
function fitsPattern(pattern: string | null | undefined, value: string): boolean {
  if (!pattern) return true;
  try {
    return new RegExp(`^(?:${pattern})$`).test(value);
  } catch {
    return true; // the server checks it again
  }
}

function fieldValue(item: CartItem, draft: Draft, key: string): string {
  return draft.tldFields?.[item.id]?.[key] ?? item.tld_fields[key] ?? "";
}

/** Every registry value given for a line, typed here or saved earlier, without blanks. */
function itemFields(item: CartItem, draft: Draft): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of item.required_fields) {
    const value = fieldValue(item, draft, f.key).trim();
    if (value) out[f.key] = value;
  }
  return out;
}

/** The most a file attached to a detail may be (the server refuses more). */
const CHECKOUT_FILE_MAX_BYTES = 5 * 1024 * 1024;

const fieldErrorKey = (item: CartItem, key: string) => `${item.id}:${key}`;

/** Required registry fields still empty, keyed `itemId:key`. */
function validateFields(items: CartItem[], draft: Draft): Record<string, string> {
  const e: Record<string, string> = {};
  for (const item of needsFields(items)) {
    for (const f of item.required_fields) {
      const value = fieldValue(item, draft, f.key).trim();
      if (f.required && !value) {
        e[fieldErrorKey(item, f.key)] =
          f.kind === "select" || f.kind === "country"
            ? "Choose one"
            : f.kind === "file"
              ? "Attach a file"
              : "Required";
      } else if (value && !fitsPattern(f.pattern, value)) {
        e[fieldErrorKey(item, f.key)] = f.pattern_message ?? "Not in the right format";
      }
    }
  }
  for (const item of needsPresence(items)) {
    if (!item.local_presence_choice) {
      e[presenceErrorKey(item)] = item.trustee_enabled
        ? "Tick the box to confirm, or choose the trustee service"
        : "Tick the box to confirm. Without a local presence this domain can't be bought";
    }
  }
  return e;
}

/** What a saved registry value reads as: the option's label or the country's name. */
function fieldDisplay(f: TldField, value: string): string {
  if (f.kind === "file") return "File attached";
  if (f.kind === "country") return countryName(value);
  return f.options.find((o) => o.value === value)?.label ?? value;
}

const EMPTY_FORM: RegistrantForm = {
  first_name: "",
  last_name: "",
  organization_name: "",
  email: "",
  dial: "44",
  phone: "",
  street: "",
  city: "",
  state: "",
  postcode: "",
  country: "GB",
};

/** Stable control ids for the registrant fields, so the error summary can link to (and focus) them. */
const REGISTRANT_FIELD_IDS: Record<keyof RegistrantForm, string> = {
  first_name: "registrant-first-name",
  last_name: "registrant-last-name",
  organization_name: "registrant-organization-name",
  email: "registrant-email",
  dial: "registrant-dial",
  phone: "registrant-phone",
  street: "registrant-street",
  city: "registrant-city",
  state: "registrant-state",
  postcode: "registrant-postcode",
  country: "registrant-country",
};

const REGISTRANT_FIELD_LABELS: Record<keyof RegistrantForm, string> = {
  first_name: "First name",
  last_name: "Last name",
  organization_name: "Organization",
  email: "Email",
  dial: "Country dialling code",
  phone: "Phone",
  street: "Street address",
  city: "City",
  state: "State / region",
  postcode: "Postcode",
  country: "Country",
};

/** The order fields appear in the form, so the summary (and focus) follows the same order. */
const REGISTRANT_FIELD_ORDER = Object.keys(REGISTRANT_FIELD_LABELS) as (keyof RegistrantForm)[];

/** One problem per blocking field, in form order, linking to (and able to focus) each one. */
function registrantProblems(errors: FormErrors): { id: string; message: string }[] {
  return REGISTRANT_FIELD_ORDER.filter((key) => errors[key]).map((key) => ({
    id: REGISTRANT_FIELD_IDS[key],
    message: `${REGISTRANT_FIELD_LABELS[key]}: ${errors[key]}`,
  }));
}

/** A stable id for a registry field's control, so the summary can link to (and focus) it too. */
function registryFieldId(item: CartItem, key: string): string {
  return `tld-field-${fieldErrorKey(item, key)}`;
}

/** One problem per missing registry field, in cart/field order. */
function registryProblems(
  items: CartItem[],
  errors: Record<string, string>,
): { id: string; message: string }[] {
  const out: { id: string; message: string }[] = [];
  for (const item of needsFields(items)) {
    for (const f of item.required_fields) {
      const message = errors[fieldErrorKey(item, f.key)];
      if (message) out.push({ id: registryFieldId(item, f.key), message: `${f.label}: ${message}` });
    }
  }
  for (const item of needsPresence(items)) {
    const message = errors[presenceErrorKey(item)];
    if (message) out.push({ id: presenceFieldId(item), message: `${item.fqdn} local presence: ${message}` });
  }
  return out;
}

function validate(f: RegistrantForm): FormErrors {
  const e: FormErrors = {};
  const need = "Required";
  if (!f.first_name.trim()) e.first_name = need;
  if (!f.last_name.trim()) e.last_name = need;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Enter a valid email address";
  if (f.phone.replace(/\D/g, "").length < 5) e.phone = "Enter a phone number";
  if (!f.street.trim()) e.street = need;
  if (!f.city.trim()) e.city = need;
  if (!f.postcode.trim()) e.postcode = need;
  for (const key of Object.keys(CONTACT_MAX) as (keyof typeof CONTACT_MAX)[]) {
    if (key !== "phone" && !e[key] && f[key].trim().length > CONTACT_MAX[key]) {
      e[key] = `Use at most ${CONTACT_MAX[key]} characters`;
    }
  }
  if (!f.country) e.country = need;
  return e;
}

function toContact(f: RegistrantForm): ContactInput {
  return {
    first_name: f.first_name.trim(),
    last_name: f.last_name.trim(),
    organization_name: f.organization_name.trim() || null,
    email: f.email.trim(),
    phone: joinPhone(f.dial, f.phone),
    street: f.street.trim(),
    city: f.city.trim(),
    state: f.state.trim() || null,
    postcode: f.postcode.trim(),
    country: f.country,
  };
}

/** Checkout for the domain cart: Figma "Add registrant details" (4040-2378) then "Review your order" (4040-2175). */
export function DomainCheckoutPage() {
  const { orgId, current } = useOrganization();
  const user = useUser();
  const [params, setParams] = useSearchParams();
  const step = params.get("step") === "review" ? "review" : "registrant";
  const cart = useCart();
  const contacts = useQuery({
    queryKey: ["domains", orgId, "contacts"],
    queryFn: () => domainsApi.contacts(orgId),
  });

  const storageKey = `cg:domains:checkout:${orgId}`;
  const business = useMemo(() => {
    const [first = "", ...rest] = (user.display_name ?? "").trim().split(/\s+/);
    return {
      first_name: first,
      last_name: rest.join(" "),
      email: user.email,
      organization_name: current.organization.name,
    };
  }, [user.display_name, user.email, current.organization.name]);

  const [draft, setDraft] = useState<Draft>(
    () =>
      safeSession.get<Draft>(storageKey) ?? {
        useBusiness: true,
        form: { ...EMPTY_FORM, ...business },
        privacy: true,
        autoRenew: true,
        paymentMethodId: null,
      },
  );
  useEffect(() => safeSession.set(storageKey, draft), [storageKey, draft]);
  // The tab keeps everything; the server draft (any device) keeps the details, never the payment choice.
  const details = useMemo<DraftDetails>(
    () => ({
      contactId: draft.contactId,
      useBusiness: draft.useBusiness,
      form: draft.form,
      privacy: draft.privacy,
      autoRenew: draft.autoRenew,
      tldFields: draft.tldFields,
      roleContacts: draft.roleContacts,
    }),
    [draft],
  );
  const serverDraft = useFormDraft("domains.checkout", details);

  const saved = contacts.data?.contacts ?? [];
  const contactId = draft.contactId === undefined ? (saved[0]?.id ?? null) : draft.contactId;
  const contact = saved.find((c) => c.id === contactId) ?? null;

  const goto = (next: "registrant" | "review") =>
    setParams((p) => {
      p.set("step", next);
      return p;
    });

  if (cart.isPending || contacts.isPending) return <FullPageSpinner />;
  if (cart.isError) return <ErrorState error={cart.error} onRetry={() => void cart.refetch()} />;
  if (cart.data.items.length === 0) return <EmptyCart />;

  const registrantReady =
    (contact !== null || Object.keys(validate(draft.form)).length === 0) &&
    Object.keys(validateFields(cart.data.items, draft)).length === 0 &&
    !draft.flaggedField;
  const props = {
    cart: cart.data,
    draft,
    setDraft,
    contact,
    contacts: saved,
    business,
    storageKey,
    serverDraft,
  };

  if (step === "review") {
    if (!registrantReady) return <Navigate to="/domains/checkout?step=registrant" replace />;
    return <ReviewStep {...props} onEdit={() => goto("registrant")} />;
  }
  return <RegistrantStep {...props} onContinue={() => goto("review")} />;
}

interface StepProps {
  cart: Cart;
  draft: Draft;
  setDraft: (update: (d: Draft) => Draft) => void;
  contact: Contact | null;
  contacts: Contact[];
  business: Pick<RegistrantForm, "first_name" | "last_name" | "email" | "organization_name">;
  storageKey: string;
  serverDraft: FormDraftApi<DraftDetails>;
}

function EmptyCart() {
  return (
    <DomainPage>
      <Breadcrumb items={[{ label: "Domains", to: "/domains" }, { label: "Checkout" }]} />
      <div className="flex flex-col items-center gap-3 rounded-[18px] border border-border bg-surface px-6 py-14 text-center">
        <span className="flex size-11 items-center justify-center rounded-xl bg-[#faf6ef]">
          <DomainIcon name="globe" size={24} />
        </span>
        <p className="text-base font-semibold text-fg">Your cart is empty</p>
        <p className="max-w-md text-sm text-fg-subtle">
          Find a domain first, then come back here to pay for it.
        </p>
        <div className="mt-1 flex flex-wrap justify-center gap-2.5">
          <Link to="/domains/find" className={buttonClass("brand", "lg")}>
            Find a domain
          </Link>
          <Link to="/domains/orders" className={buttonClass("secondary", "lg")}>
            Order history
          </Link>
        </div>
      </div>
    </DomainPage>
  );
}

function forDomains(items: CartItem[]): string {
  const first = items[0]?.fqdn ?? "";
  return items.length > 1 ? `${first} and ${items.length - 1} more` : first;
}

// ── step 1: registrant ──────────────────────────────────────────────────────
function RegistrantStep({
  cart,
  draft,
  setDraft,
  contact,
  contacts,
  business,
  serverDraft,
  onContinue,
}: StepProps & { onContinue: () => void }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [errors, setErrors] = useState<FormErrors>({});
  // Bumped on every blocked submit, so the summary is mounted (and announced) again even if the same
  // problems remain.
  const [attempt, setAttempt] = useState(0);
  const registry = needsFields(cart.items);
  // A field checkout found missing is pointed at straight away.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>(() => {
    const flagged = draft.flaggedField;
    if (!flagged) return {};
    const e: Record<string, string> = {};
    if (flagged.key === "local_presence") {
      for (const item of needsPresence(cart.items)) {
        if (!item.local_presence_choice) e[presenceErrorKey(item)] = flagged.message;
      }
      return e;
    }
    for (const item of registry) {
      if (item.required_fields.some((x) => x.key === flagged.key)) {
        e[fieldErrorKey(item, flagged.key)] = "The registry needs this before it can register the name";
      }
    }
    return e;
  });
  const saveFields = useMutation({
    mutationFn: ({ item, values }: { item: CartItem; values: Record<string, string> }) =>
      domainsApi.setCartFields(orgId, item.id, values),
    onSuccess: (next) => qc.setQueryData(domainKeys.cart(orgId), next),
    onError: (err, { item }) => {
      // A value the server refused (wrong format…) is shown next to its field, not only in the alert below.
      const refused = err instanceof ApiError ? err.fieldErrors[0] : undefined;
      if (refused && item.required_fields.some((x) => x.key === refused.field)) {
        setFieldErrors((e) => ({ ...e, [fieldErrorKey(item, refused.field)]: refused.message }));
      }
    },
  });
  // Files attached here (id → the name it was uploaded as), and the detail being uploaded now.
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<string | null>(null);
  const attach = async (item: CartItem, key: string, file: File) => {
    const id = fieldErrorKey(item, key);
    const refuse = (message: string) => setFieldErrors((e) => ({ ...e, [id]: message }));
    if (file.size > CHECKOUT_FILE_MAX_BYTES) return refuse("Keep the file under 5 MB");
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name)) return refuse("Attach a PDF, JPG or PNG file");
    setUploading(id);
    try {
      const stored = await domainsApi.uploadCheckoutFile(orgId, file);
      setFileNames((n) => ({ ...n, [stored.id]: stored.name }));
      setField(item, key, stored.id, true);
    } catch (err) {
      refuse(err instanceof Error ? err.message : "The file couldn't be uploaded. Try again");
    } finally {
      setUploading(null);
    }
  };
  const choosePresence = useMutation({
    mutationFn: ({ item, choice }: { item: CartItem; choice: "confirmed" | "trustee" | "none" }) =>
      domainsApi.setLocalPresence(orgId, item.id, choice),
    onSuccess: (next, { item }) => {
      qc.setQueryData(domainKeys.cart(orgId), next);
      setFieldErrors((e) => {
        const copy = { ...e };
        delete copy[presenceErrorKey(item)];
        return copy;
      });
    },
  });
  // Details given with this contact for these endings last time are filled in (never over what is typed).
  useEffect(() => {
    if (!contact || registry.length === 0) return;
    let live = true;
    const endings = [...new Set(registry.map((i) => endingOf(i.fqdn).slice(1)))];
    domainsApi
      .contactTldFields(orgId, contact.id, endings)
      .then(({ values }) => {
        if (!live) return;
        setDraft((d) => {
          const next = { ...d.tldFields };
          for (const item of registry) {
            for (const [key, value] of Object.entries(values[endingOf(item.fqdn).slice(1)] ?? {})) {
              const known = item.required_fields.some((x) => x.key === key);
              if (known && !(next[item.id]?.[key] ?? item.tld_fields[key])) {
                next[item.id] = { ...next[item.id], [key]: value };
              }
            }
          }
          return { ...d, tldFields: next };
        });
      })
      .catch(() => undefined); // prefilling is a convenience: the fields are simply empty
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when another contact is chosen
  }, [contact?.id, orgId]);
  const setField = (item: CartItem, key: string, value: string, save: boolean) => {
    const next = { ...draft.tldFields, [item.id]: { ...draft.tldFields?.[item.id], [key]: value } };
    setDraft((d) => ({ ...d, tldFields: next, flaggedField: null }));
    setFieldErrors((e) => {
      const copy = { ...e };
      delete copy[fieldErrorKey(item, key)];
      return copy;
    });
    if (save) saveFields.mutate({ item, values: itemFields(item, { ...draft, tldFields: next }) });
  };
  /** Text fields are saved when the person leaves them, if they changed. */
  const saveIfChanged = (item: CartItem, key: string) => {
    if (fieldValue(item, draft, key).trim() !== (item.tld_fields[key] ?? "")) {
      saveFields.mutate({ item, values: itemFields(item, draft) });
    }
  };
  const f = draft.form;
  const set = (patch: Partial<RegistrantForm>) => {
    setDraft((d) => ({ ...d, form: { ...d.form, ...patch } }));
    setErrors((e) => {
      const next = { ...e };
      for (const k of Object.keys(patch) as (keyof RegistrantForm)[]) delete next[k];
      return next;
    });
  };
  const toggleBusiness = (on: boolean) =>
    setDraft((d) => ({
      ...d,
      useBusiness: on,
      form: on
        ? { ...d.form, ...business }
        : { ...d.form, first_name: "", last_name: "", email: "", organization_name: "" },
    }));
  const pick = (id: string | null) => setDraft((d) => ({ ...d, contactId: id }));

  const submit = () => {
    const found = contact ? {} : validate(f);
    const missing = validateFields(cart.items, draft);
    setErrors(found);
    setFieldErrors(missing);
    setAttempt((n) => n + 1);
    if (Object.keys(found).length > 0 || Object.keys(missing).length > 0) {
      const firstId = [...registrantProblems(found), ...registryProblems(cart.items, missing)][0]?.id;
      if (firstId) focusControl(firstId);
      return;
    }
    // Keep the cart in step with what's typed, so it's there if this page is reopened.
    for (const item of registry) {
      const values = itemFields(item, draft);
      const saved = item.tld_fields;
      const same =
        Object.keys(values).length === Object.keys(saved).length &&
        Object.entries(values).every(([k, v]) => saved[k] === v);
      if (!same) saveFields.mutate({ item, values });
    }
    setDraft((d) => ({ ...d, flaggedField: null }));
    onContinue();
  };

  const problems = [...registrantProblems(errors), ...registryProblems(cart.items, fieldErrors)];

  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "Find domain", to: "/domains/find" },
          { label: "Registrant" },
        ]}
      />
      <Heading
        size={24}
        tile={
          <HeadTile>
            <FileText className="size-5 text-brand-500" />
          </HeadTile>
        }
        title="Add registrant details"
        subtitle={`Required by ICANN to complete registration for ${forDomains(cart.items)}`}
      />
      <DraftOffer draft={serverDraft} onContinue={(values) => setDraft((d) => ({ ...d, ...values }))} />
      <DraftSavedNote savedAt={serverDraft.savedAt} />
      {problems.length > 0 && <ErrorSummary key={attempt} problems={problems} />}

      <TwoColumns
        main={
          <>
            {contacts.length > 0 && (
              <Section
                title="Registrant"
                subtitle="Use details you've registered domains with before, or add new ones"
              >
                <div className="flex flex-col gap-2">
                  {contacts.map((c) => (
                    <ChoiceRow
                      key={c.id}
                      active={contact?.id === c.id}
                      onClick={() => pick(c.id)}
                      title={c.full_name + (c.organization_name ? ` · ${c.organization_name}` : "")}
                      detail={`${c.email} · ${c.address}`}
                    />
                  ))}
                  <ChoiceRow
                    active={contact === null}
                    onClick={() => pick(null)}
                    title="Someone else"
                    detail="Enter new registrant details"
                  />
                </div>
              </Section>
            )}

            {contact === null && (
              <Section>
                <div className="flex items-center gap-3">
                  <p className="min-w-0 flex-1 text-sm font-medium text-fg">
                    Use my account and business details
                  </p>
                  <Toggle
                    size="lg"
                    checked={draft.useBusiness}
                    onChange={toggleBusiness}
                    label="Use my account and business details"
                  />
                </div>
                <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-2">
                  <Field
                    size="lg"
                    label="First name"
                    error={errors.first_name}
                    required
                    id={REGISTRANT_FIELD_IDS.first_name}
                  >
                    {(control) => (
                      <Input
                        size="lg"
                        {...control}
                        value={f.first_name}
                        maxLength={CONTACT_MAX.first_name}
                        autoComplete="given-name"
                        invalid={Boolean(errors.first_name)}
                        onChange={(e) => set({ first_name: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field
                    size="lg"
                    label="Last name"
                    error={errors.last_name}
                    required
                    id={REGISTRANT_FIELD_IDS.last_name}
                  >
                    {(control) => (
                      <Input
                        size="lg"
                        {...control}
                        value={f.last_name}
                        maxLength={CONTACT_MAX.last_name}
                        autoComplete="family-name"
                        invalid={Boolean(errors.last_name)}
                        onChange={(e) => set({ last_name: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field size="lg" label="Organization" optional>
                    {(control) => (
                      <Input
                        size="lg"
                        {...control}
                        value={f.organization_name}
                        maxLength={CONTACT_MAX.organization_name}
                        autoComplete="organization"
                        onChange={(e) => set({ organization_name: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field
                    size="lg"
                    label="Email"
                    error={errors.email}
                    required
                    id={REGISTRANT_FIELD_IDS.email}
                  >
                    {(control) => (
                      <Input
                        size="lg"
                        {...control}
                        type="email"
                        value={f.email}
                        maxLength={CONTACT_MAX.email}
                        autoComplete="email"
                        invalid={Boolean(errors.email)}
                        onChange={(e) => set({ email: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field
                    size="lg"
                    label="Phone"
                    error={errors.phone}
                    required
                    id={REGISTRANT_FIELD_IDS.phone}
                  >
                    {(control) => (
                      <div className="flex gap-2">
                        <select
                          aria-label="Country dialling code"
                          value={f.dial}
                          onChange={(e) => set({ dial: e.target.value })}
                          className={cx(CONTROL_LG, "!w-[92px] shrink-0 pr-1")}
                        >
                          {DIAL_OPTIONS.map((d) => (
                            <option key={d} value={d}>
                              +{d}
                            </option>
                          ))}
                        </select>
                        <Input
                          size="lg"
                          {...control}
                          type="tel"
                          value={f.phone}
                          autoComplete="tel-national"
                          inputMode="tel"
                          placeholder="7700 900123"
                          className="min-w-0 flex-1"
                          invalid={Boolean(errors.phone)}
                          onChange={(e) => set({ phone: e.target.value })}
                        />
                      </div>
                    )}
                  </Field>
                  <Field
                    size="lg"
                    label="Street address"
                    error={errors.street}
                    required
                    id={REGISTRANT_FIELD_IDS.street}
                  >
                    {(control) => (
                      <Input
                        size="lg"
                        {...control}
                        value={f.street}
                        maxLength={CONTACT_MAX.street}
                        autoComplete="street-address"
                        invalid={Boolean(errors.street)}
                        onChange={(e) => set({ street: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field size="lg" label="City" error={errors.city} required id={REGISTRANT_FIELD_IDS.city}>
                    {(control) => (
                      <Input
                        size="lg"
                        {...control}
                        value={f.city}
                        maxLength={CONTACT_MAX.city}
                        autoComplete="address-level2"
                        invalid={Boolean(errors.city)}
                        onChange={(e) => set({ city: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field size="lg" label="State / region" optional>
                    {(control) => (
                      <Input
                        size="lg"
                        {...control}
                        value={f.state}
                        maxLength={CONTACT_MAX.state}
                        autoComplete="address-level1"
                        onChange={(e) => set({ state: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field
                    size="lg"
                    label="Postcode"
                    error={errors.postcode}
                    required
                    id={REGISTRANT_FIELD_IDS.postcode}
                  >
                    {(control) => (
                      <Input
                        size="lg"
                        {...control}
                        value={f.postcode}
                        maxLength={CONTACT_MAX.postcode}
                        autoComplete="postal-code"
                        invalid={Boolean(errors.postcode)}
                        onChange={(e) => set({ postcode: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field
                    size="lg"
                    label="Country"
                    error={errors.country}
                    required
                    id={REGISTRANT_FIELD_IDS.country}
                  >
                    {(control) => (
                      <select
                        {...control}
                        value={f.country}
                        autoComplete="country"
                        onChange={(e) => {
                          const country = e.target.value;
                          // Follow the country with the dialling code until a number is typed.
                          set(f.phone ? { country } : { country, dial: DIAL_CODES[country] ?? f.dial });
                        }}
                        className={CONTROL_LG}
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c} value={c}>
                            {countryName(c)}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                </div>
              </Section>
            )}

            {draft.flaggedField && (
              <Notice tone="warning" title="The registry needs a few more details">
                {draft.flaggedField.message}. Fill in the highlighted field below, then continue to review.
                Nothing was charged.
              </Notice>
            )}
            {registry.map((item) => (
              <RegistryFields
                key={item.id}
                item={item}
                draft={draft}
                errors={fieldErrors}
                onChange={setField}
                onBlur={saveIfChanged}
                onFile={attach}
                uploading={uploading}
                fileNames={fileNames}
              />
            ))}
            {registry.length > 0 && <ErrorAlert error={saveFields.error} />}
            {needsPresence(cart.items).map((item) => (
              <LocalPresence
                key={item.id}
                item={item}
                error={fieldErrors[presenceErrorKey(item)]}
                pending={choosePresence.isPending}
                onChoose={(choice) => choosePresence.mutate({ item, choice })}
              />
            ))}
            <ErrorAlert error={choosePresence.error} />

            <OtherContacts cart={cart} draft={draft} setDraft={setDraft} contacts={contacts} />

            <Section>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fg">
                    Enable privacy protection{" "}
                    <HelpHint helpKey={HELP_KEYS.domains.whoisPrivacy} label="WHOIS privacy" />
                  </p>
                  <p className="text-xs text-fg-subtle">
                    Hides your personal details from the public WHOIS lookup. Included at no extra cost.
                  </p>
                </div>
                <Toggle
                  size="lg"
                  checked={draft.privacy}
                  onChange={(privacy) => setDraft((d) => ({ ...d, privacy }))}
                  label="Enable privacy protection"
                />
              </div>
            </Section>

            <div className="flex flex-wrap gap-2.5">
              <Button variant="brand" size="lg" onClick={submit}>
                Continue to review
              </Button>
              <Link to="/domains/find" className={buttonClass("secondary", "lg")}>
                Back
              </Link>
            </div>
          </>
        }
        side={
          <Section title="Your order">
            {cart.items.map((i) => (
              <div key={i.id} className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-fg">{i.fqdn}</p>
                  <p className="text-xs text-fg-subtle">
                    {termLabel(i)} · auto-renew {draft.autoRenew ? "on" : "off"}
                  </p>
                </div>
                <span className="text-sm font-semibold text-fg">
                  {formatMoney(i.net_cents - i.trustee_line_cents, cart.currency)}
                </span>
              </div>
            ))}
            {cart.items
              .filter((i) => i.trustee_line_cents > 0)
              .map((i) => (
                <div key={`${i.id}-trustee`} className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-fg">Trustee service for {i.fqdn}</p>
                    <p className="text-xs text-fg-subtle">Renews with the domain</p>
                  </div>
                  <span className="text-sm font-semibold text-fg">
                    {formatMoney(i.trustee_line_cents, cart.currency)}
                  </span>
                </div>
              ))}
            <p className="text-xs text-fg-subtle">
              Prices exclude VAT. ICANN requires accurate registrant details — inaccurate info can risk
              suspension later.
            </p>
          </Section>
        }
      />
    </DomainPage>
  );
}

/** How the customer meets a local-presence requirement: confirm it, or buy the trustee service (if sold). */
function LocalPresence({
  item,
  error,
  pending,
  onChoose,
}: {
  item: CartItem;
  error: string | undefined;
  pending: boolean;
  onChoose: (choice: "confirmed" | "trustee" | "none") => void;
}) {
  const confirmed = item.local_presence_choice === "confirmed";
  const trustee = item.local_presence_choice === "trustee";
  const describedBy = error ? `${presenceFieldId(item)}-error` : undefined;
  return (
    <Section
      title={`Local presence for ${item.fqdn}`}
      subtitle={`The ${endingOf(item.fqdn)} registry needs the registrant to have an address in its country`}
    >
      <div role="group" aria-label={`Local presence for ${item.fqdn}`} className="flex flex-col gap-3">
        <label className="flex items-start gap-3 text-sm text-fg">
          <input
            id={presenceFieldId(item)}
            type="checkbox"
            className="mt-0.5 size-4"
            checked={confirmed}
            disabled={pending}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            onChange={(e) => onChoose(e.target.checked ? "confirmed" : "none")}
          />
          <span>{"The registrant meets this TLD's local presence requirement"}</span>
        </label>
        {item.trustee_enabled && item.trustee_cents != null && (
          <label className="flex items-start gap-3 text-sm text-fg">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={trustee}
              disabled={pending}
              aria-describedby={describedBy}
              onChange={(e) => onChoose(e.target.checked ? "trustee" : "none")}
            />
            <span>
              Buy the trustee service for{" "}
              <span className="font-semibold">{formatMoney(item.trustee_cents, "GBP")}</span> a year, excl.
              VAT.
              <span className="block text-xs text-fg-subtle">
                It is charged with the domain and again each year it renews.
              </span>
            </span>
          </label>
        )}
        {!item.trustee_enabled && !confirmed && (
          <p className="text-xs text-fg-subtle">
            We don&apos;t offer a trustee service for this ending. Without a local address, this domain
            can&apos;t be bought: remove it from your order to buy the others.
          </p>
        )}
        {error && (
          <p id={describedBy} role="alert" className="text-sm text-[#d6342f]">
            {error}
          </p>
        )}
      </div>
    </Section>
  );
}

/** The registry's extra questions for one name, saved to the cart as they're answered. */
function RegistryFields({
  item,
  draft,
  errors,
  onChange,
  onBlur,
  onFile,
  uploading,
  fileNames,
}: {
  item: CartItem;
  draft: Draft;
  errors: Record<string, string>;
  onChange: (item: CartItem, key: string, value: string, save: boolean) => void;
  onBlur: (item: CartItem, key: string) => void;
  onFile: (item: CartItem, key: string, file: File) => void;
  uploading: string | null;
  fileNames: Record<string, string>;
}) {
  return (
    <Section
      title={`Registry details for ${item.fqdn}`}
      subtitle={`The ${endingOf(item.fqdn)} registry needs these before it registers the name`}
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-2">
        {item.required_fields.map((field) => {
          const value = fieldValue(item, draft, field.key);
          const error = errors[fieldErrorKey(item, field.key)];
          return (
            <Field
              key={field.key}
              id={registryFieldId(item, field.key)}
              label={field.label}
              optional={!field.required}
              required={field.required}
              hint={field.description ?? undefined}
              error={error}
            >
              {(control) =>
                field.kind === "file" ? (
                  <div className="flex flex-col gap-1.5">
                    <input
                      {...control}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/png,image/jpeg"
                      disabled={uploading === fieldErrorKey(item, field.key)}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onFile(item, field.key, file);
                        e.target.value = ""; // choosing the same file again still counts
                      }}
                      className={cx(CONTROL_LG, error && "border-[#d6342f]")}
                    />
                    <p className="text-xs text-fg-subtle" aria-live="polite">
                      {uploading === fieldErrorKey(item, field.key)
                        ? "Uploading…"
                        : value
                          ? `Attached: ${fileNames[value] ?? "a file you added earlier"}. Choose another to replace it.`
                          : "PDF, JPG or PNG, up to 5 MB."}
                    </p>
                  </div>
                ) : field.kind === "select" || field.kind === "country" ? (
                  <select
                    {...control}
                    value={value}
                    onChange={(e) => onChange(item, field.key, e.target.value, true)}
                    className={cx(CONTROL_LG, error && "border-[#d6342f]")}
                  >
                    <option value="">{field.kind === "country" ? "Choose a country" : "Choose…"}</option>
                    {field.kind === "country"
                      ? [...COUNTRIES, ...(value && !COUNTRIES.includes(value) ? [value] : [])].map((c) => (
                          <option key={c} value={c}>
                            {countryName(c)}
                          </option>
                        ))
                      : field.options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                  </select>
                ) : (
                  <Input
                    size="lg"
                    {...control}
                    value={value}
                    type={field.kind === "date" ? "date" : "text"}
                    maxLength={255}
                    invalid={Boolean(error)}
                    onChange={(e) => onChange(item, field.key, e.target.value, field.kind === "date")}
                    onBlur={() => onBlur(item, field.key)}
                  />
                )
              }
            </Field>
          );
        })}
      </div>
    </Section>
  );
}

function termLabel(i: CartItem): string {
  if (i.kind === "transfer") return "Transfer · adds 1 year";
  return `${i.years} ${i.years === 1 ? "year" : "years"}`;
}

function ChoiceRow({
  active,
  onClick,
  title,
  detail,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cx(
        "flex items-center gap-3 rounded-xl border p-3 text-left",
        active ? "border-brand-500 bg-brand-50" : "border-border hover:bg-muted",
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-fg">{title}</span>
        <span className="block truncate text-xs text-fg-subtle">{detail}</span>
      </span>
      <span
        className={cx(
          "size-4 shrink-0 rounded-full border-2",
          active ? "border-brand-500 bg-brand-500 shadow-[inset_0_0_0_2px_white]" : "border-[#d4cac4]",
        )}
      />
    </button>
  );
}

// ── step 2: review ──────────────────────────────────────────────────────────
function ReviewStep({
  cart,
  draft,
  setDraft,
  contact,
  contacts,
  storageKey,
  serverDraft,
  onEdit,
}: StepProps & { onEdit: () => void }) {
  const { orgId, can } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const methods = usePaymentMethods();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [problem, setProblem] = useState<{
    tone: "warning" | "danger";
    title: string;
    body: ReactNode;
    /** Names to look for alternatives to (a domain taken before payment). */
    alternatives?: string[];
    /** Prices moved: the new total needs the customer's confirmation, so the button says so. */
    confirmAgain?: boolean;
    /** Another purchase is on this already: point at the orders instead of inviting a second payment. */
    toOrders?: boolean;
  } | null>(null);
  const canPay = can("billing:manage");

  // A card the person chose not to save isn't listed with the cards on file, so it is kept here.
  const [oneTime, setOneTime] = useState<PaymentMethod | null>(null);
  // Apple Pay or Google Pay chosen in the payment window: the total is approved in its sheet.
  const [wallet, setWallet] = useState<WalletChoice | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState<CouponPreview | null>(null);
  // Why the code in the field was refused (by the check, or when paying): shown under the field.
  const [couponError, setCouponError] = useState<string | null>(null);
  const usable = (methods.data?.payment_methods ?? []).filter(payableNow);
  const method: PaymentMethod | undefined = wallet
    ? undefined
    : ((oneTime && oneTime.id === draft.paymentMethodId ? oneTime : undefined) ??
      usable.find((m) => m.id === draft.paymentMethodId) ??
      defaultMethod(usable));

  const setCart = (next: Cart) => qc.setQueryData(domainKeys.cart(orgId), next);
  const couponPreview = useMutation({
    mutationFn: () => domainsApi.previewCoupon(orgId, couponCode.trim()),
    onSuccess: (next) => {
      setCoupon(next);
      setCouponError(null);
    },
    onError: (err) => {
      setCoupon(null);
      setCouponError(couponFieldError(err));
    },
  });
  // One key per attempt to pay: if the connection drops and the page asks again, the server returns the order
  // the first request made instead of buying twice. It is only replaced when the server has given a definite
  // refusal (nothing was charged), never after "no answer" or "still processing".
  // It is kept for the tab (with the rest of the form) so that reloading the page doesn't lose it: a reload
  // after "no answer" must ask again with the same key, not start a second payment.
  const keyStorage = `${storageKey}:checkout-key`;
  const attempt = useRef(safeSession.get<string>(keyStorage) ?? newCheckoutKey());
  useEffect(() => safeSession.set(keyStorage, attempt.current), [keyStorage]);
  const placeOrder = async (body: CheckoutInput) => {
    try {
      return await domainsApi.checkout(orgId, body, attempt.current);
    } catch (err) {
      if (attemptEnded(err)) {
        attempt.current = newCheckoutKey();
        safeSession.set(keyStorage, attempt.current);
      }
      throw err;
    }
  };
  const years = useMutation({
    mutationFn: ({ id, n }: { id: string; n: number }) => domainsApi.setCartYears(orgId, id, n),
    onSuccess: (next) => {
      setCart(next);
      setCoupon(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => domainsApi.removeFromCart(orgId, id),
    onSuccess: (next) => {
      setCart(next);
      setCoupon(null);
    },
  });

  const roles = chosenRoles(cart.items, draft.roleContacts, contacts);
  const order = {
    contact_id: contact?.id ?? null,
    contact: contact ? null : toContact(draft.form),
    ...(roles.length > 0 && {
      contacts: Object.fromEntries(roles.map(([role, c]) => [role, { contact_id: c.id }])),
    }),
    privacy: draft.privacy,
    auto_renew: draft.autoRenew,
    tld_fields: Object.fromEntries(
      needsFields(cart.items).map((i) => [i.fqdn, itemFields(i, draft)] as const),
    ),
    // The total on screen: if the registry's price moved, checkout stops and nothing is charged.
    expected_total_cents: coupon?.total_cents ?? cart.total_cents,
    coupon_code: coupon?.code ?? null,
  };
  const placed = async (done: Order) => {
    safeSession.remove(storageKey);
    safeSession.remove(keyStorage);
    serverDraft.clear();
    await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
    navigate(`/domains/orders/${done.id}`, { replace: true });
  };
  const purchase = useMutation({
    mutationFn: () => placeOrder({ ...order, payment_method_id: method?.id ?? null }),
    onMutate: () => setProblem(null),
    onSuccess: placed,
    onError: async (err) => {
      const message = err instanceof Error ? err.message : "";
      const reason = reasonOf(err);
      if (isCouponReason(reason)) {
        // The server checked the code again and refused it: nothing was charged. The discount comes off the
        // screen and the reason goes under the field, so the total shown is the one that would be charged.
        setCoupon(null);
        setCouponError(couponFieldError(err));
        setProblem({
          tone: "warning",
          title: "Your promotion code can't be used",
          body: `${message.replace(/\.$/, "")}. Nothing was charged. Your total is back to ${formatMoney(cart.total_cents, cart.currency)}: remove the code or enter another, then confirm again.`,
        });
        return;
      }
      switch (reason) {
        case "PRICE_CHANGED": {
          const { total_cents, changes } = priceChange(err);
          await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
          setProblem({
            tone: "warning",
            title: "A price changed — check the new total",
            confirmAgain: true,
            body: (
              <>
                <p>The registry changed a price since you added it. Nothing was charged.</p>
                {changes.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-0.5">
                    {changes.map((c) => (
                      <li key={c.fqdn}>
                        <span className="font-semibold break-all text-fg">{c.fqdn}</span>:{" "}
                        {c.old_cents !== null && <>{formatMoney(c.old_cents, cart.currency)} → </>}
                        <span className="font-semibold text-fg">
                          {formatMoney(c.new_cents, cart.currency)}
                        </span>
                        <span className="text-fg-subtle"> excl. VAT</span>
                      </li>
                    ))}
                  </ul>
                )}
                {total_cents !== null && (
                  <p className="mt-2">
                    New total:{" "}
                    <span className="font-semibold text-fg">{formatMoney(total_cents, cart.currency)}</span>{" "}
                    incl. VAT. Confirm again to pay it.
                  </p>
                )}
              </>
            ),
          });
          break;
        }
        case "TLD_FIELDS_REQUIRED": {
          const field = err instanceof ApiError ? err.fieldErrors[0]?.field : undefined;
          await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
          setDraft((d) => ({
            ...d,
            flaggedField: { key: field ?? "", message: message.replace(/\.$/, "") },
          }));
          onEdit();
          break;
        }
        case "UNSUPPORTED_PERIOD":
          await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
          setProblem({
            tone: "warning",
            title: "Choose another term",
            body: `${message.replace(/\.$/, "")}. Pick one of the terms offered above, then confirm again. Nothing was charged.`,
          });
          break;
        case "PRICING_STALE":
        case "PRICE_UNDER_REVIEW":
        case "PRICE_QUOTE_UNAVAILABLE":
        case "PREMIUM_QUOTE_REQUIRED":
          setProblem({
            tone: "warning",
            title: "We can't confirm a price right now",
            body: `${message.replace(/\.$/, "")}. Nothing was charged and your cart is saved — try again in a few minutes.`,
          });
          break;
        case "TRUSTEE_UNAVAILABLE":
        case "LOCAL_PRESENCE_REQUIRED": {
          if (
            reason === "LOCAL_PRESENCE_REQUIRED" &&
            !(err instanceof ApiError && err.fieldErrors[0]?.field === "local_presence")
          ) {
            // The ending can't be bought online at all: handled below with the other refusals.
            setProblem({
              tone: "danger",
              title: "A domain can't be bought online",
              body: (
                <>
                  {message.replace(/\.$/, "")}. Remove it from your order to buy the rest. Nothing was
                  charged. For help, email{" "}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="font-semibold text-brand-500 hover:underline"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </>
              ),
            });
            break;
          }
          // The registrant step asks again: nothing was charged.
          await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
          setDraft((d) => ({
            ...d,
            flaggedField: { key: "local_presence", message: message.replace(/\.$/, "") },
          }));
          onEdit();
          break;
        }
        case "PREMIUM_NOT_SOLD":
        case "TRANSFER_NOT_SUPPORTED":
          setProblem({
            tone: "danger",
            title:
              reason === "TRANSFER_NOT_SUPPORTED"
                ? "This domain can't be transferred to us"
                : "A domain can't be bought online",
            body: (
              <>
                {message.replace(/\.$/, "")}. Remove it from your order to buy the rest. Nothing was charged.
                For help, email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-brand-500 hover:underline">
                  {SUPPORT_EMAIL}
                </a>
                .
              </>
            ),
          });
          break;
        case "DOMAIN_TAKEN":
          await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
          setProblem({
            tone: "danger",
            title: "A domain was just taken",
            body: message,
            alternatives: takenKeywords(err),
          });
          break;
        case "AVAILABILITY_UNKNOWN":
          setProblem({ tone: "warning", title: "We couldn't confirm availability", body: message });
          break;
        case "DOMAIN_BEING_BOUGHT":
        case "DOMAIN_ALREADY_MANAGED":
        case "CHECKOUT_IN_PROGRESS": {
          // Nothing was charged here. An earlier request may be about to empty the cart: look again.
          await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
          const shown = holdProblem(reason, message);
          if (shown) setProblem(shown);
          break;
        }
        case "BILLING_PERMISSION_REQUIRED":
          setProblem({ tone: "danger", title: "You can't pay for domains", body: message });
          break;
        case "NETWORK_ERROR":
          // No answer: the payment may have gone through. The key is kept (attemptEnded), so confirming again
          // returns that order instead of charging twice — say so, or the customer may avoid the safe retry.
          setProblem({
            tone: "warning",
            title: "We couldn't confirm your payment",
            body: "We couldn't reach our server, so we can't tell yet whether your payment went through. It's safe to confirm again: if it already went through, you'll see that order and won't be charged twice. You can also check your orders.",
            toOrders: true,
          });
          break;
        case "PAYMENT_DECLINED":
          // A declined one-time card has already been removed; another card is needed.
          if (method && !method.saved) {
            setOneTime(null);
            setDraft((d) => ({ ...d, paymentMethodId: null }));
          }
          setProblem({
            tone: "danger",
            title: "Your payment was declined",
            body: `${message.replace(/\.$/, "")}. Nothing was registered — try another card.`,
          });
          break;
        case "PAYMENT_METHOD_REQUIRED":
          setProblem({ tone: "warning", title: "Add a card to continue", body: message });
          setPaymentOpen(true);
          break;
        case "CONTACT_NOT_FOUND":
          setDraft((d) => ({ ...d, contactId: null }));
          onEdit();
          break;
        case "CART_EMPTY":
          await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
          break;
      }
    },
  });
  const handled = [
    "PRICE_CHANGED",
    "DOMAIN_TAKEN",
    "DOMAIN_BEING_BOUGHT",
    "DOMAIN_ALREADY_MANAGED",
    "CHECKOUT_IN_PROGRESS",
    "AVAILABILITY_UNKNOWN",
    "BILLING_PERMISSION_REQUIRED",
    "NETWORK_ERROR",
    "PAYMENT_DECLINED",
    "PAYMENT_METHOD_REQUIRED",
    "TLD_FIELDS_REQUIRED",
    "UNSUPPORTED_PERIOD",
    "PRICING_STALE",
    "PRICE_UNDER_REVIEW",
    "PRICE_QUOTE_UNAVAILABLE",
    "PREMIUM_QUOTE_REQUIRED",
    "PREMIUM_NOT_SOLD",
    "LOCAL_PRESENCE_REQUIRED",
    "TRANSFER_NOT_SUPPORTED",
  ];
  // A refused promotion code is shown under its field and in the notice already: not a third time here.
  const genericError =
    purchase.error &&
    !handled.includes(reasonOf(purchase.error) ?? "") &&
    !isCouponReason(reasonOf(purchase.error))
      ? purchase.error
      : null;

  // Paid in the wallet's sheet: it shows the total; the order is then placed with the approval.
  const walletCalls = {
    amountCents: coupon?.total_cents ?? cart.total_cents,
    recurring: null,
    start: () => domainsApi.startWalletCheckout(orgId, coupon?.code ?? null),
    finish: (reference: string) =>
      placeOrder({
        ...order,
        payment_reference: reference,
        save_card: wallet?.save ?? false,
        make_default: wallet?.makeDefault ?? false,
      }),
    onPaid: (done: Order) => void placed(done),
  };
  const { slot: appleSlot, ...apple } = useWalletPayment(orgId, "apple_pay", walletCalls);
  const { slot: googleSlot, ...google } = useWalletPayment(orgId, "google_pay", walletCalls);
  const walletPay = wallet?.wallet === "apple_pay" ? apple : google;

  const registrant = contact
    ? { name: contact.full_name, email: contact.email }
    : { name: `${draft.form.first_name} ${draft.form.last_name}`.trim(), email: draft.form.email };
  const busy = years.isPending || remove.isPending || purchase.isPending;
  const fieldsMissing = Object.keys(validateFields(cart.items, draft)).length > 0;
  const single = cart.items.length === 1;
  const registry = needsFields(cart.items);
  const now = useNow(30_000);
  // The soonest a held price lapses; after that, checkout prices the cart again.
  const heldUntil = cart.items
    .map((i) => i.quote_expires_at)
    .filter((t): t is string => t !== null)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
  const heldLabel =
    heldUntil && new Date(heldUntil).getTime() > now
      ? civilDay(heldUntil) === civilDay(now)
        ? clockTime(heldUntil)
        : `${clockTime(heldUntil)} on ${longDate(heldUntil)}`
      : null;

  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "Find domain", to: "/domains/find" },
          { label: "Review order" },
        ]}
      />
      <Heading
        size={24}
        tile={
          <HeadTile>
            <ReceiptText className="size-5 text-brand-500" />
          </HeadTile>
        }
        title="Review your order"
        subtitle="Nothing is charged until you confirm below"
      />

      <TwoColumns
        main={
          <>
            <div role="status" aria-live="polite" className="contents">
              {problem && (
                <Notice tone={problem.tone} title={problem.title}>
                  {problem.body}
                  {problem.toOrders && (
                    <span className="mt-2 flex">
                      <Link to="/domains/orders" className={buttonClass("secondary", "lg")}>
                        View your orders
                      </Link>
                    </span>
                  )}
                  {problem.alternatives && problem.alternatives.length > 0 && (
                    <span className="mt-2 flex flex-wrap gap-2">
                      {problem.alternatives.map((k) => (
                        <Link
                          key={k}
                          to={`/domains/find?q=${encodeURIComponent(k)}`}
                          className={buttonClass("secondary", "lg")}
                        >
                          Find an alternative to {k}
                        </Link>
                      ))}
                    </span>
                  )}
                </Notice>
              )}
            </div>
            {cart.items.map((item) => (
              <Section
                key={item.id}
                title={item.kind === "transfer" ? "Domain transfer" : "Domain"}
                action={
                  single ? (
                    <Link
                      to={`/domains/find?q=${encodeURIComponent(item.fqdn.split(".")[0] ?? item.fqdn)}`}
                      className={buttonClass("secondary", "lg")}
                    >
                      Change
                    </Link>
                  ) : (
                    <Button
                      size="lg"
                      variant="secondary"
                      loading={remove.isPending && remove.variables === item.id}
                      disabledReason={busy ? "Wait for your cart to finish updating" : undefined}
                      onClick={() => remove.mutate(item.id)}
                    >
                      Remove
                    </Button>
                  )
                }
              >
                <Rows>
                  <Row label="Domain">
                    <span className="font-semibold break-all text-fg">{item.fqdn}</span>
                    {item.premium && (
                      <Badge size="md" tone="warning">
                        Premium
                      </Badge>
                    )}
                  </Row>
                  <Row label="Term">
                    {item.kind === "transfer" ? (
                      <span className="font-semibold text-fg">1 year added on transfer</span>
                    ) : (
                      <select
                        aria-label={`Term for ${item.fqdn}`}
                        value={item.years}
                        disabled={busy}
                        onChange={(e) => years.mutate({ id: item.id, n: Number(e.target.value) })}
                        className="rounded-lg border border-border bg-surface px-2 py-1 text-sm font-semibold text-fg"
                      >
                        {termOptions(item.periods, item.years).map((n) => (
                          <option key={n} value={n}>
                            {n} {n === 1 ? "year" : "years"}
                          </option>
                        ))}
                      </select>
                    )}
                  </Row>
                  <Row label={item.kind === "transfer" ? "Transfer price" : "First-year price"}>
                    {hasPromotion(item) && <OfferPill promotion={item} />}
                    {hasPromotion(item) && item.standard_register_cents !== null && (
                      <UsualPrice cents={item.standard_register_cents} currency={cart.currency} />
                    )}
                    <span className="font-semibold text-fg">
                      {formatMoney(item.register_cents, cart.currency)}
                    </span>
                  </Row>
                  {renewalCents(item) !== null && (
                    <Row label="Renews at">
                      <span className="font-semibold text-fg">
                        {formatMoney(renewalCents(item)!, cart.currency)} / year
                      </span>
                    </Row>
                  )}
                  <Row label={single ? "Auto-renew" : "Auto-renew (all domains in this order)"}>
                    <Toggle
                      size="lg"
                      checked={draft.autoRenew}
                      onChange={(autoRenew) => setDraft((d) => ({ ...d, autoRenew }))}
                      label="Auto-renew"
                    />
                  </Row>
                </Rows>
                {hasPromotion(item) && (
                  <p className="text-xs text-fg-subtle">
                    The offer is for the first year only.{" "}
                    {item.years > 1 ? "Further years and renewals" : "Renewals"} are at the usual price
                    {renewalCents(item) !== null &&
                      ` of ${formatMoney(renewalCents(item)!, cart.currency)} a year`}
                    .
                  </p>
                )}
              </Section>
            ))}
            <ErrorAlert error={years.error ?? remove.error} />

            <Section
              title="Registrant"
              action={
                <Button size="lg" variant="secondary" onClick={onEdit}>
                  Edit
                </Button>
              }
            >
              <p className="text-sm text-[#5c5551]">
                {registrant.name} · {registrant.email} · Privacy protection{" "}
                {draft.privacy ? "enabled" : "off"}
              </p>
              {roles.map(([role, c]) => (
                <p key={role} className="text-sm text-[#5c5551]">
                  {ROLE_LABELS[role]}: {c.full_name} · {c.email}
                </p>
              ))}
            </Section>

            {registry.length > 0 && (
              <Section
                title="Registry details"
                action={
                  <Button size="lg" variant="secondary" onClick={onEdit}>
                    Edit
                  </Button>
                }
              >
                <div className="flex flex-col gap-2">
                  {registry.map((item) => (
                    <p key={item.id} className="text-sm text-[#5c5551]">
                      <span className="font-semibold break-all text-fg">{item.fqdn}</span>
                      {item.required_fields
                        .filter((f) => fieldValue(item, draft, f.key).trim())
                        .map((f) => (
                          <span key={f.key}>
                            {" · "}
                            {f.label}: {fieldDisplay(f, fieldValue(item, draft, f.key).trim())}
                          </span>
                        ))}
                    </p>
                  ))}
                </div>
              </Section>
            )}

            <Section
              title="Payment method"
              action={
                (method || wallet) && (
                  <Button size="lg" variant="secondary" onClick={() => setPaymentOpen(true)}>
                    Change
                  </Button>
                )
              }
            >
              {wallet ? (
                <div className="flex items-center gap-3">
                  <PayIcon method={wallet.wallet} className="size-[18px] text-fg-muted" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-fg">{PAY_METHOD_LABELS[wallet.wallet]}</p>
                    <p className="text-xs text-fg-subtle">
                      You approve the total in {PAY_METHOD_LABELS[wallet.wallet]} when you complete the
                      purchase
                      {wallet.save ? " · Saved for future payments" : " · This payment only, not saved"}
                    </p>
                  </div>
                </div>
              ) : methods.isPending ? (
                <p className="text-sm text-fg-subtle">Loading your cards…</p>
              ) : method ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <CardBadge brand={method.brand} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-fg">{method.label}</p>
                      <p className="text-xs text-fg-subtle">
                        Expires {String(method.exp_month).padStart(2, "0")}/
                        {String(method.exp_year).slice(-2)}
                        {!method.saved
                          ? " · This payment only, not saved"
                          : method.is_default
                            ? " · Your default card"
                            : ""}
                      </p>
                    </div>
                  </div>
                  {!method.saved && (
                    <>
                      <RenewalRiskWarning />
                      {draft.autoRenew && (
                        <p className="text-xs text-fg-subtle">
                          Auto-renew is a separate setting and stays on. It can only take payment if a card is
                          saved on your account when the renewal is due — you can add one any time in Billing.
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="min-w-0 flex-1 text-sm text-fg-subtle">
                    No card on file yet. Add one to pay for this order.
                  </p>
                  <Button
                    variant="brand"
                    size="lg"
                    requires="billing:manage"
                    onClick={() => setPaymentOpen(true)}
                  >
                    Add a card
                  </Button>
                </div>
              )}
              <ErrorAlert error={methods.error} />
            </Section>

            <PromotionCode
              code={couponCode}
              onCode={(next) => {
                setCouponCode(next);
                setCoupon(null);
                setCouponError(null);
                couponPreview.reset();
              }}
              onApply={() => couponPreview.mutate()}
              applied={coupon}
              error={couponError}
              pending={couponPreview.isPending}
              disabled={busy}
            />
          </>
        }
        side={
          <Section title="Total due today">
            <div className="flex flex-col gap-2">
              {cart.items.map((i) => (
                <div key={i.id} className="flex items-start gap-3 text-[13px]">
                  <span className="min-w-0 flex-1 break-all text-[#5c5551]">
                    {i.fqdn} · {i.kind === "transfer" ? "transfer" : `${i.years}yr`}
                  </span>
                  <span className="font-semibold text-fg">
                    {formatMoney(
                      i.net_cents - i.trustee_line_cents - (coupon?.allocations[i.fqdn] ?? 0),
                      cart.currency,
                    )}
                  </span>
                </div>
              ))}
              {cart.items
                .filter((i) => i.trustee_line_cents > 0)
                .map((i) => (
                  <div key={`${i.id}-trustee`} className="flex items-start gap-3 text-[13px]">
                    <span className="min-w-0 flex-1 break-all text-[#5c5551]">
                      Trustee service · {i.fqdn}
                    </span>
                    <span className="font-semibold text-fg">
                      {formatMoney(i.trustee_line_cents, cart.currency)}
                    </span>
                  </div>
                ))}
              {coupon && (
                <div className="flex items-start gap-3 text-[13px] text-[#007955]">
                  <span className="flex-1">{coupon.code}</span>
                  <span className="font-semibold">−{formatMoney(coupon.discount_cents, cart.currency)}</span>
                </div>
              )}
              <div className="flex items-start gap-3 text-[13px]">
                <span className="flex-1 text-[#5c5551]">VAT</span>
                <span className="font-semibold text-fg">
                  {formatMoney(coupon?.tax_cents ?? cart.vat_cents, cart.currency)}
                </span>
              </div>
              <NoVatNote items={cart.items} />
            </div>
            <div className="flex items-center border-t border-border pt-3.5">
              <span className="flex-1 text-sm font-semibold text-fg">Total</span>
              <span className="text-xl font-semibold text-fg">
                {formatMoney(coupon?.total_cents ?? cart.total_cents, cart.currency)}
              </span>
            </div>
            {canPay ? (
              <>
                {wallet ? (
                  // The wallet's own button: its sheet shows the total. Mounted while chosen (unmounting
                  // would reload it); "Complete purchase" stands in while it can't show.
                  <>
                    <ErrorAlert error={walletPay.error} />
                    {walletPay.retry && (
                      <button
                        type="button"
                        onClick={walletPay.retry}
                        disabled={walletPay.saving}
                        aria-busy={walletPay.saving}
                        className="text-sm font-semibold text-brand-500 hover:underline disabled:opacity-60"
                      >
                        Try again
                      </button>
                    )}
                    <div
                      key={wallet.wallet}
                      className={cx("relative h-11", walletPay.status === "unavailable" && "hidden")}
                    >
                      {walletPay.status === "loading" && (
                        <div className="absolute inset-0 animate-pulse rounded-[10px] bg-muted" aria-hidden />
                      )}
                      <div
                        ref={wallet.wallet === "apple_pay" ? appleSlot : googleSlot}
                        className={cx(
                          (walletPay.saving || busy || fieldsMissing) && "pointer-events-none opacity-60",
                        )}
                      />
                    </div>
                    {walletPay.status === "unavailable" && (
                      <Hint tone="warning" title={`${PAY_METHOD_LABELS[wallet.wallet]} isn't available here`}>
                        Choose a card instead with Change.
                      </Hint>
                    )}
                    {walletPay.saving && (
                      <p role="status" className="text-xs text-fg-subtle">
                        Completing your purchase…
                      </p>
                    )}
                  </>
                ) : (
                  <Button
                    variant="brand"
                    size="lg"
                    className="w-full"
                    loading={purchase.isPending}
                    disabledReason={
                      !method
                        ? "Add a payment method to complete the purchase"
                        : busy
                          ? "Wait for your cart to finish updating"
                          : fieldsMissing
                            ? "Fill in the registration details above first"
                            : undefined
                    }
                    onClick={() => purchase.mutate()}
                  >
                    {problem?.confirmAgain
                      ? `Confirm new total of ${formatMoney(coupon?.total_cents ?? cart.total_cents, cart.currency)}`
                      : "Complete purchase"}
                  </Button>
                )}
                {heldLabel && (
                  <p className="text-center text-xs text-fg-subtle">
                    {cart.items.length > 1 ? "Prices" : "Price"} held until {heldLabel}
                  </p>
                )}
                <PoweredByStripe provider={methods.data?.provider} className="mx-auto" />
                {!method && !methods.isPending && (
                  <p className="text-xs text-fg-subtle">Add a payment method to complete the purchase.</p>
                )}
                <p className="text-[11px] text-fg-subtle">
                  Domains aren't reserved until registered: we check each one is still available before
                  charging, and again right before registering it. If one goes in between, it's refunded in
                  full. By continuing you agree to the domain registration terms.
                </p>
              </>
            ) : (
              <Hint tone="warning" title="Ask someone who manages billing">
                Only people who manage billing can pay for domains. Your cart is saved — ask the business
                owner to complete the purchase, or to give you billing access.
              </Hint>
            )}
            <ErrorAlert error={genericError} />
          </Section>
        }
      />

      <PaymentMethodModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        selectedId={method?.id ?? null}
        title={method ? "Change payment method" : "Add a payment method"}
        oneTime
        onWallet={(choice) => {
          setWallet(choice);
          setPaymentOpen(false);
          if (problem?.title === "Add a card to continue") setProblem(null);
        }}
        onChosen={(m) => {
          setWallet(null);
          setOneTime(m.saved ? null : m);
          setDraft((d) => ({ ...d, paymentMethodId: m.id }));
          setPaymentOpen(false);
          if (problem?.title === "Add a card to continue") setProblem(null);
        }}
      />
    </DomainPage>
  );
}

/** What a line renews at: never the first-year offer (the usual price when the registry didn't say). */
function renewalCents(item: CartItem): number | null {
  return item.renew_cents ?? (hasPromotion(item) ? item.standard_register_cents : null);
}

function Rows({ children }: { children: ReactNode }) {
  return <div className="flex flex-col [&>*+*]:border-t [&>*+*]:border-border">{children}</div>;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-[46px] items-center gap-3 py-2.5 text-sm">
      <span className="text-[#5c5551]">{label}</span>
      <span className="flex-1" />
      <span className="flex min-w-0 items-center justify-end gap-2 text-right">{children}</span>
    </div>
  );
}
